/**
 * useDynamicManifest - Dynamically generates PWA manifest with organization branding
 * 
 * Updates manifest.json, apple-touch-icon, and meta tags based on current organization
 */

import { useEffect, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useDeviceOrganization } from './useDeviceOrganization';
import { RESERVED_PATHS } from '../lib/routeConfig';

/**
 * Creates a padded icon with white background and centered logo
 */
async function createPaddedIcon(logoUrl: string, size: number): Promise<string> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) return reject('No canvas context');
    
    // White background
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, size, size);
    
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      // Calculate centered position with 15% padding
      const padding = size * 0.15;
      const maxSize = size - (padding * 2);
      
      // Maintain aspect ratio
      const scale = Math.min(maxSize / img.width, maxSize / img.height);
      const drawWidth = img.width * scale;
      const drawHeight = img.height * scale;
      const x = (size - drawWidth) / 2;
      const y = (size - drawHeight) / 2;
      
      ctx.drawImage(img, x, y, drawWidth, drawHeight);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => reject('Failed to load image');
    img.src = logoUrl;
  });
}

export function useDynamicManifest() {
  const { organizationName, organizationSlug, logoUrl, logoLightUrl, logoDarkUrl } = useDeviceOrganization();
  const location = useLocation();
  const blobUrlRef = useRef<string | null>(null);

  // Extract slug from multiple sources (priority order):
  // 1. Current URL path (e.g., /boxmeal-gavle-ab) - only if not a reserved path
  // 2. organizationSlug from device context
  // 3. sessionStorage org_context
  // 4. sessionStorage last_slug_path
  // 5. Global __BOOTSTRAP_SLUG__
  const effectiveSlug = useMemo(() => {
    // 1. URL path (only if it's a valid slug, not a reserved path)
    const pathSegment = location.pathname.replace(/\/+$/, '').split('/')[1];
    if (pathSegment && !RESERVED_PATHS.has(pathSegment)) {
      return pathSegment;
    }
    
    // 2. Organization slug from device
    if (organizationSlug) return organizationSlug;
    
    // 3. sessionStorage org_context
    try {
      const raw = sessionStorage.getItem('org_context');
      if (raw) {
        const ctx = JSON.parse(raw);
        if (ctx?.org_slug) return ctx.org_slug;
      }
    } catch { /* ignore */ }
    
    // 4. sessionStorage last_slug_path (remove leading slash if present)
    const lastSlug = sessionStorage.getItem('last_slug_path');
    if (lastSlug) return lastSlug.replace(/^\//, '');
    
    // 5. Global __BOOTSTRAP_SLUG__
    const globalSlug = (window as any).__BOOTSTRAP_SLUG__;
    if (globalSlug) return globalSlug;
    
    return null;
  }, [location.pathname, organizationSlug]);

  useEffect(() => {
    // Clean up previous blob URL
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }

    const sourceLogoUrl = logoLightUrl || logoUrl;
    const appName = organizationName || 'Ziplink Handel';
    // Use first word of org name (e.g., "Boxmeal" from "Boxmeal Gävle AB")
    const shortName = organizationName 
      ? organizationName.split(' ')[0].slice(0, 12) 
      : 'Ziplink';

    async function generateManifest() {
      let iconUrl192 = '/icon-192x192.png';
      let iconUrl512 = '/icon-512x512.png';

      // Generate padded icons if we have an org logo
      if (sourceLogoUrl) {
        try {
          const [icon192, icon512] = await Promise.all([
            createPaddedIcon(sourceLogoUrl, 192),
            createPaddedIcon(sourceLogoUrl, 512)
          ]);
          iconUrl192 = icon192;
          iconUrl512 = icon512;
        } catch (e) {
          console.warn('Failed to generate padded icons, using defaults', e);
        }
      }

      // Dynamic start_url based on organization slug (from URL or device)
      const startUrl = effectiveSlug ? `/${effectiveSlug}` : '/';

      // Generate dynamic manifest
      const manifest = {
        name: appName,
        short_name: shortName,
        description: `Handla hos ${appName}`,
        start_url: startUrl,
        scope: '/',
        display: 'standalone',
        background_color: '#FFFFFF',
        theme_color: '#0F172A',
        orientation: 'portrait',
        icons: [
          { src: iconUrl192, sizes: '192x192', type: 'image/png', purpose: 'any' },
          { src: iconUrl512, sizes: '512x512', type: 'image/png', purpose: 'any' },
          { src: iconUrl192, sizes: '192x192', type: 'image/png', purpose: 'maskable' },
          { src: iconUrl512, sizes: '512x512', type: 'image/png', purpose: 'maskable' }
        ]
      };

      // Create blob URL for manifest
      const blob = new Blob([JSON.stringify(manifest)], { type: 'application/json' });
      const manifestUrl = URL.createObjectURL(blob);
      blobUrlRef.current = manifestUrl;

      // Update manifest link
      const manifestLink = document.getElementById('pwa-manifest');
      if (manifestLink) {
        manifestLink.setAttribute('href', manifestUrl);
      }

      // Update apple-touch-icon (iOS uses this for home screen icon)
      const appleTouchIcon = document.getElementById('apple-touch-icon');
      if (appleTouchIcon) {
        appleTouchIcon.setAttribute('href', iconUrl192);
      }

      // Update apple-mobile-web-app-title meta tag
      let titleMeta = document.querySelector('meta[name="apple-mobile-web-app-title"]');
      if (titleMeta) {
        titleMeta.setAttribute('content', shortName);
      } else {
        titleMeta = document.createElement('meta');
        titleMeta.setAttribute('name', 'apple-mobile-web-app-title');
        titleMeta.setAttribute('content', shortName);
        document.head.appendChild(titleMeta);
      }

      // Update document title
      document.title = appName;
    }

    generateManifest();

    // Cleanup on unmount
    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [organizationName, effectiveSlug, logoUrl, logoLightUrl, logoDarkUrl]);
}
